import { combineReducers, compose } from '@reduxjs/toolkit';

import { ENABLE_APPLE_PAY } from 'utils/globals';

import slice from './cart_payment_method.slice';

import giftCert from 'domains/gift_cert';
import cartBilling from 'domains/cart_billing';

import type { CheckoutRootState } from 'store/checkout_store';
import type { PaymentType } from './cart_payment_method.types';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectAllowNetTerms = (state: LocalState) => state.allowNetTerms;
const selectAllowBraintree = (state: LocalState) => state.hasBraintree;

const selectPaymentMethodStatus = (state: LocalState) => state.status;
const selectPaymentMethodToken = (state: LocalState) => state.tokenizePayload;
const selectPaymentMethodError = (state: LocalState) => state.error;

const selectPaymentMethodType = (state: LocalState) => state.selectedType;

const selectGiftCertUsage = (state: CheckoutRootState) => {
  const cert = giftCert.selectors.valid(state);
  const billing = cartBilling.selectors.billing(state);

  const usage: { usage?: number; isWhollyCovered: boolean } = {
    usage: undefined,
    isWhollyCovered: false,
  };

  if (!!cert && !!billing) {
    usage.usage = billing.total >= cert.balance ? cert.balance : billing.total;
    usage.isWhollyCovered = usage.usage === billing.total;
  }

  return usage;
};

/**
 * This should be the singular determination if the combination of
 * payment method, customer, and cart satisifies the conditions to
 * submit an order
 */
const selectValidPaymentType = (state: CheckoutRootState): PaymentType | null => {
  const { isWhollyCovered } = selectGiftCertUsage(state);
  const selectedType = selectPaymentMethodType(localState(state));

  const allowNetTerms = selectAllowNetTerms(localState(state));
  const isUsingNetTerms = selectedType === 'net_terms';

  const allowBraintree = selectAllowBraintree(localState(state));
  const braintreePayment = selectPaymentMethodToken(localState(state));

  const applePaySelected = selectedType === 'apple_pay';

  // Some customers have special pricing defined in labworks, but do not
  // have net terms. These cases must be allowed to submit without other methods
  const mustUseDeferredPayment = !allowNetTerms && !allowBraintree;

  if (isWhollyCovered) return 'wholly_gift_certificate';
  if (mustUseDeferredPayment) return 'deferred_labworks';
  if (allowNetTerms && isUsingNetTerms) return 'net_terms';
  if (allowBraintree && !!braintreePayment) return 'credit_card';
  // if (allowBraintree && applePaySelected) return 'apple_pay';
  if (ENABLE_APPLE_PAY && applePaySelected) return 'apple_pay';

  return null;
};

const selectHasValidPaymentMethod = (state: CheckoutRootState) => !!selectValidPaymentType(state);

export default {
  allowNetTerms: compose(selectAllowNetTerms, localState),
  allowBraintree: compose(selectAllowBraintree, localState),
  status: compose(selectPaymentMethodStatus, localState),
  token: compose(selectPaymentMethodToken, localState),
  type: compose(selectPaymentMethodType, localState),
  error: compose(selectPaymentMethodError, localState),
  // Already globalized
  giftCert: selectGiftCertUsage,
  validType: selectValidPaymentType,
  hasValidType: selectHasValidPaymentMethod,
};
